import React, { useEffect, useMemo, useState } from "react";
import { useRecruitment } from "../../../../../hooks/useSubjects";
import {
  useClassroomForGeneralAssessmentSetting,
  useGeneralAssessmentAccessorRole,
  useGeneralConfigApplySetttings,
  useGeneralConfiguration,
  useImportGeneralConfiguration,
} from "../../../../../hooks/useAssessment";
import Button from "../../../../shared/Button";
import swal from "sweetalert";
import { URL } from "../../../../../../../utils/Constants";
import CustomModal from "../../../../shared/CustomModal";
import { Spinner } from "reactstrap";
import AssessmentConfiguration from "./AssessmentConfiguration";
import CommentConfiguration from "./CommentConfiguration";
import Select from "../../../../shared/Select";
import { v4 as uuidv4 } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";

const GeneralConfiguration = ({ payload, closeModal }) => {
  const { assessorRole } = useGeneralAssessmentAccessorRole();
  const { staffList, getStaffList } = useRecruitment();
  const { classroomForGeneralAssessmentSetting } =
    useClassroomForGeneralAssessmentSetting(payload);
  const { generalConfiguration, loading, saveGeneralConfiguration } =
    useGeneralConfiguration(payload);
  const {
    data,
    loading: isImporting,
    importGeneralConfiguration,
  } = useImportGeneralConfiguration();

  const [assessmentFormData, setAssessmentFormData] = useState({
    header: "",
    assessmentType: "overallAssessment",
    overallScaleId: "",
    rubricsScaleId: "",
    accessor: "",
    subRoleId: "",
    roleCode: "",
    roleId: "",
  });
  const [assessmentTableData, setAssessmentTableData] = useState([]);
  const [commentFormData, setCommentFormData] = useState({
    name: "",
    maxLen: "",
    minLen: "",
    textType: "letter",
    commenter: "",
    subRoleId: "",
    roleCode: "",
    roleId: "",
  });
  const [commentTableData, setCommentTableData] = useState([]);
  const [rubricsConfig, setRubricsConfig] = useState({});
  const [deletedItem, setDeletedItem] = useState({
    deleteConfigIds: [],
    deleteRubricsIds: [],
    deleteCommentIds: [],
  });
  const [importSettings, setImportSettings] = useState([]);
  const [warningModal, setWarningModal] = useState(false);
  const [applySettinsModal, setApplySettingsModal] = useState(false);
  const [applySettingsTo, setApplySettingsTo] = useState([]);

  const { loading: isApplying, applySettingsHandler } =
    useGeneralConfigApplySetttings();

  const classRoomForGeneralSettingOptions = useMemo(() => {
    if (classroomForGeneralAssessmentSetting?.length === 0) return [];

    return classroomForGeneralAssessmentSetting?.map((classroom) => ({
      label: `Class ${classroom?.yearLevelName} - ${classroom?.name}`,
      value: classroom.id,
      yearLevelId: classroom?.yearLevelId,
    }));
  }, [classroomForGeneralAssessmentSetting]);

  //assessorRoleOption
  const assessorRoleOption = useMemo(() => {
    if (assessorRole?.length === 0) return [];

    return assessorRole?.map((item) => ({
      label: item?.name,
      value: item?.id,
      roleCode: item?.roleCode,
      subRoleId: item?.subRoleId,
    }));
  }, [assessorRole]);

  //staffOptions
  const staffsOption = useMemo(() => {
    if (staffList?.length === 0) return [];

    return staffList?.map((item) => ({
      label: item?.people?.name,
      value: item?.id,
    }));
  }, [staffList]);

  //assessor options list
  const assessorOptions = useMemo(() => {
    return [
      {
        label: "Roles",
        options: assessorRoleOption,
      },
      {
        label: "Staff",
        options: staffsOption,
      },
    ];
  }, [assessorRoleOption, staffsOption]);

  //assessor config to extract data faster
  const assessorConfig = useMemo(() => {
    const map = {};

    if (assessorRole?.length) {
      assessorRole.forEach((item) => {
        map[item?.id] = item;
      });
    }

    if (staffList?.length) {
      staffList.forEach((item) => {
        map[item?.id] = item;
      });
    }

    return map;
  }, [assessorRole, staffList]);

  //general configuration submit handler
  const handleSubmit = async () => {
    const generalConfigPayload = assessmentTableData?.map((item) => ({
      header: item?.header,
      assessmentType: item?.assessmentType,
      overallScaleId: item?.overallScaleId,
      rubricsScaleId: item?.rubricsScaleId,
      accessor: item?.accessor ?? null,
      hasRole: item?.accessor ? false : true,
      subRoleId: item?.subRoleId ?? null,
      roleCode: item?.roleCode ?? null,
      rubrics: rubricsConfig[item?.fId ?? item?.id] ?? [],
      id: item?.id,
    }));

    const commentsPayload = commentTableData?.map((item) => ({
      name: item?.name,
      minLen: item?.minLen,
      maxLen: item?.maxLen,
      textType: item?.textType,
      hasRole: item?.commenter ? false : true,
      commenter: item?.commenter ?? null,
      subRoleId: item?.subRoleId ?? null,
      roleCode: item?.roleCode ?? null,
      id: item?.id,
    }));

    const params = {
      assessmentDetails: payload,
      generalConfig: generalConfigPayload,
      comments: commentsPayload,
      deleteConfigIds: deletedItem.deleteConfigIds,
      deleteRubricsIds: deletedItem.deleteRubricsIds,
      deleteCommentIds: deletedItem.deleteCommentIds,
    };

    try {
      await saveGeneralConfiguration(params);
      setDeletedItem({
        deleteConfigIds: [],
        deleteRubricsIds: [],
        deleteCommentIds: [],
      });
      setImportSettings(null);
      setWarningModal(false);
    } catch (error) {
      console.error("Update Failed", error);
    }
  };

  //import select change handler
  const handleImportSelectChange = async (option) => {
    const value = {
      classroomId: option?.value,
      yearLevelId: option?.yearLevelId,
    };

    setImportSettings(value);

    if (!option) return;

    const params = {
      assessmentId: payload?.assessmentId,
      educationLevelDivisionId: payload?.educationLevelDivisionId,
      educationLevelId: payload?.educationLevelId,
      toClassroomId: payload?.classroomId,
      toYearLevelId: payload?.yearLevelId,
      fromClassroomId: value?.classroomId,
      fromYearLevelId: value?.yearLevelId,
    };

    try {
      await importGeneralConfiguration(params);
    } catch (error) {
      console.error("Import Failed", error);
    }
  };

  //populate response data
  const populateData = (data) => {
    setCommentTableData(data?.comments ?? []);

    const generalConfig = (data?.generalConfig || [])?.map((item) => {
      if (item?.id) return item;

      return {
        ...item,
        fId: uuidv4(),
      };
    });

    setAssessmentTableData(generalConfig);

    const rubricsAssessment = generalConfig?.reduce((acc, arr) => {
      const key = arr?.id ?? arr?.fId;

      if (!acc[key]) {
        acc[key] = [];
      }

      if (arr?.rubrics?.length > 0) {
        acc[key].push(...arr.rubrics);
      }

      return acc;
    }, {});

    setRubricsConfig(rubricsAssessment ?? []);
  };

  const handleApplySettingsTo = async () => {
    if (!applySettingsTo?.length) return;

    const applyTo = applySettingsTo?.map((item) => ({
      classroomId: item?.value,
      yearLevelId: item?.yearLevelId,
    }));

    const applyFrom = {
      assessmentId: payload?.assessmentId,
      educationLevelDivisionId: payload?.educationLevelDivisionId,
      educationLevelId: payload?.educationLevelId,
      classroomId: payload?.classroomId,
      yearLevelId: payload?.yearLevelId,
    };

    const params = {
      applyFrom,
      applyTo,
    };

    try {
      await applySettingsHandler(params);
      setApplySettingsTo([]);
      setApplySettingsModal(false);
    } catch (error) {
      console.error("Apply Settings Failed: ", error);
    }
  };

  //fetch staff list
  useEffect(() => {
    getStaffList();
  }, []);

  //fetch imported data
  useEffect(() => {
    if (data !== null) {
      populateData(data);
    }
  }, [data]);

  //fetch existing data
  useEffect(() => {
    if (generalConfiguration !== null) {
      populateData(generalConfiguration);
    }
  }, [generalConfiguration]);

  return (
    <>
      <AssessmentConfiguration
        assessmentFormData={assessmentFormData}
        setAssessmentFormData={setAssessmentFormData}
        assessorOptions={assessorOptions}
        loading={loading}
        assessmentTableData={assessmentTableData}
        setAssessmentTableData={setAssessmentTableData}
        assessorRoleOption={assessorRoleOption}
        rubricsConfig={rubricsConfig}
        setRubricsConfig={setRubricsConfig}
        assessorConfig={assessorConfig}
        setDeletedItem={setDeletedItem}
        isImporting={isImporting}
      />

      <CommentConfiguration
        commentFormData={commentFormData}
        setCommentFormData={setCommentFormData}
        assessorOptions={assessorOptions}
        loading={loading}
        commentTableData={commentTableData}
        setCommentTableData={setCommentTableData}
        assessorRoleOption={assessorRoleOption}
        assessorConfig={assessorConfig}
        setDeletedItem={setDeletedItem}
        isImporting={isImporting}
      />

      <div style={{ justifyContent: "space-between" }} className="flex">
        <Button variant="outline" onClick={closeModal}>
          Cancel
        </Button>
        <div style={{ marginTop: "24px" }} className="action-btns end">
          <Button
            onClick={() => {
              const isConfigUpdate = assessmentTableData?.some(
                (item) => item?.id,
              );
              if (isConfigUpdate) {
                setWarningModal(true);
              } else {
                handleSubmit();
              }
            }}
            disabled={loading.insert}
          >
            {loading?.insert ? (
              <Spinner size={"sm"} color="white" />
            ) : (
              "Save General Configuration"
            )}
          </Button>
          {(generalConfiguration?.generalConfig?.length > 0 ||
            generalConfiguration?.comments?.length > 0) && (
            <Tooltip title="This feature lets you apply the saved scale and comments configured in this section to other relevant sections within the same class.">
              <div>
                <Button
                  disabled={
                    !generalConfiguration?.generalConfig?.length &&
                    !generalConfiguration?.comments?.length
                  }
                  variant="outline"
                  onClick={() => setApplySettingsModal(true)}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span>Apply To</span>
                    <FontAwesomeIcon icon={faCircleInfo} />
                  </div>
                </Button>
              </div>
            </Tooltip>
          )}
        </div>
      </div>

      <CustomModal
        isOpen={warningModal}
        toggle={() => setWarningModal(false)}
        modalHeader={"Update Configuration"}
        children={
          <div>
            <p>
              Are you sure? This will update the assessment and comment
              settings. Changes may affect existing assessments.
            </p>
            <div className="action-btns end">
              <Button onClick={() => setWarningModal(false)} variant="outline">
                Cancel
              </Button>
              <Button disabled={loading.insert} onClick={handleSubmit}>
                {loading?.insert ? (
                  <Spinner size={"sm"} color="white" />
                ) : (
                  "Update"
                )}
              </Button>
            </div>
          </div>
        }
      />

      <CustomModal
        isOpen={applySettinsModal}
        toggle={() => {
          setApplySettingsModal(false);
          setApplySettingsTo([]);
        }}
        modalHeader={"Apply Settings"}
        children={
          <div>
            <Select
              options={classRoomForGeneralSettingOptions?.filter(
                (opt) => opt.value !== payload?.classroomId,
              )}
              value={applySettingsTo}
              isMulti={true}
              styles={{ control: { minWidth: "200px" } }}
              placeholder={"Select Section"}
              onChange={(selectedOptions) =>
                setApplySettingsTo(selectedOptions)
              }
            />

            <div style={{ marginTop: "16px" }} className="action-btns end">
              <Button
                disabled={isApplying || applySettingsTo?.length === 0}
                size="sm"
                onClick={handleApplySettingsTo}
              >
                {isApplying ? (
                  <Spinner size="sm" color="white" />
                ) : (
                  "Apply Settings"
                )}
              </Button>
            </div>

            <div className="arr-note">
              <strong>Note: </strong>
              <ol
                style={{
                  padding: "10px 0px 0px 10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <li>
                  Please save the current changes before applying settings to
                  other section.
                </li>
                <li>
                  Please note that the existing configuration will be overridden
                  when using this feature. Additionally, if any scale of the
                  target sections are already recorded, those sections will not
                  be listed in the selection options.
                </li>
              </ol>
            </div>
          </div>
        }
      />
    </>
  );
};

export default GeneralConfiguration;
